import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Pencil, Loader2, Save, X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import QuoteBuilder from '@/components/QuoteBuilder';
import { computeRowPricing } from '@/lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const CAT_LABEL = {
  part_and_labour: 'Parts + Labour',
  part: 'Parts',
  labour: 'Labour',
  tyre: 'Tyre',
  simple: 'Item',
};

const cleanRows = (rows) =>
  (rows || [])
    .filter((r) => (r.description || '').trim())
    .map((r) => ({
      line_id: r.line_id,
      description: r.description,
      category: r.category,
      quantity: Number(r.quantity || 1),
      parts_supplier: (r.parts_supplier || '').trim() || null,
      parts_cost_price: r.parts_cost_price === '' || r.parts_cost_price == null ? null : Number(r.parts_cost_price),
      parts_price: r.parts_price === '' || r.parts_price == null ? null : Number(r.parts_price),
      parts_margin_pct: r.parts_margin_pct === '' || r.parts_margin_pct == null ? null : Number(r.parts_margin_pct),
      labour_hours: r.labour_hours === '' || r.labour_hours == null ? null : Number(r.labour_hours),
      labour_rate: r.labour_rate === '' || r.labour_rate == null ? null : Number(r.labour_rate),
      tyre_flat_rate: r.tyre_flat_rate === '' || r.tyre_flat_rate == null ? null : Number(r.tyre_flat_rate),
      line_total: computeRowPricing(r).line_total,
    }));

/**
 * iter188 — The quote that was built in the Booking popup's Quote Builder
 * and SMS'd to the customer. The SM couldn't recall what was quoted; this
 * surfaces every line item (with prices) right inside the Job Card and lets
 * the SM edit it (same Quote Builder used everywhere). Hidden from mechanics.
 */
export default function JobCardQuoteSection({ jobCard, user, onUpdate }) {
  const quoteId = jobCard?.quote_id;
  const isMechanic = user && ['mechanic', 'technician'].includes(user.role);

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadQuote = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setQuote(d);
    } catch (e) {
      console.error('Failed to load quote', e);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  if (isMechanic || !quoteId) return null;

  const lineItems = quote?.line_items || [];
  // For the read-only view, trust the STORED line_total (that's exactly
  // what was quoted & SMS'd to the customer). Only recompute when a line
  // has no stored total (legacy rows).
  const lineTotalOf = (li) =>
    li.line_total != null ? Number(li.line_total) : (computeRowPricing(li).line_total || 0);
  const total = lineItems.reduce((s, li) => s + lineTotalOf(li), 0);

  const startEdit = () => {
    setRows(
      lineItems.map((li) => {
        const qty = li.quantity ?? 1;
        // Legacy/flat lines carry a stored line_total but NO granular
        // pricing (parts_price / labour_rate all null). Handing those to
        // the Quote Builder as-is makes it recompute to $0 on save and
        // silently wipe the quote. Hydrate them as a plain part priced so
        // the recompute is a no-op that preserves the amount the customer
        // was quoted. (No-op for normal quotes that already carry pricing.)
        const stored = Number(li.line_total) || 0;
        const recomputed = computeRowPricing(li).line_total || 0;
        const isFlatLegacy = stored > 0.005 && recomputed < 0.005;
        return {
          line_id: li.line_id || `qli_${Math.random().toString(36).slice(2, 10)}`,
          description: li.description || '',
          category: isFlatLegacy ? 'part' : (li.category || 'part_and_labour'),
          quantity: qty,
          parts_supplier: li.parts_supplier ?? '',
          parts_cost_price: li.parts_cost_price ?? '',
          parts_price: isFlatLegacy ? round2(stored / (Number(qty) || 1)) : (li.parts_price ?? ''),
          parts_margin_pct: isFlatLegacy ? 0 : (li.parts_margin_pct ?? ''),
          labour_hours: isFlatLegacy ? '' : (li.labour_hours ?? ''),
          labour_rate: isFlatLegacy ? '' : (li.labour_rate ?? ''),
          tyre_flat_rate: isFlatLegacy ? '' : (li.tyre_flat_rate ?? ''),
          line_total: li.line_total ?? 0,
        };
      }),
    );
    setEditing(true);
  };

  const saveQuote = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_items: cleanRows(rows) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const updated = await r.json();
      setQuote(updated);
      setEditing(false);
      toast.success('Quote updated');
      // Reload the JC so the linked mirror + kanban tile stay in sync.
      try {
        const jr = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`, { credentials: 'include' });
        if (jr.ok) {
          const freshJc = await jr.json();
          onUpdate && onUpdate(freshJc);
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(`Failed to save quote — ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const smsBody = quote?.last_sms_body || quote?.customer_message;

  return (
    <section className="border border-emerald-200 rounded-md" data-testid="jc-quote-section">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-emerald-200 bg-emerald-50 rounded-t-md">
        <FileText className="h-4 w-4 text-emerald-700" />
        <h4 className="text-sm font-semibold text-emerald-900">
          Quote sent to customer
          {quote?.quote_number && (
            <span className="ml-2 text-[11px] font-mono font-normal text-emerald-700">{quote.quote_number}</span>
          )}
        </h4>
        {quote?.status && (
          <Badge variant="outline" className="text-[10px] capitalize border-emerald-300 text-emerald-800">
            {quote.status}
          </Badge>
        )}
        {!editing && !loading && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs ml-auto border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            onClick={startEdit}
            data-testid="jc-quote-edit-btn"
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit quote
          </Button>
        )}
      </header>

      <div className="p-3">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-emerald-600">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : editing ? (
          <div className="space-y-3" data-testid="jc-quote-editor">
            <QuoteBuilder rows={rows} onChange={setRows} showOrp={true} />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                disabled={saving}
                onClick={() => setEditing(false)}
                data-testid="jc-quote-cancel-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                disabled={saving}
                onClick={saveQuote}
                data-testid="jc-quote-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Save quote
              </Button>
            </div>
          </div>
        ) : lineItems.length === 0 ? (
          <p className="text-xs text-slate-400 italic" data-testid="jc-quote-empty">
            This quote has no line items yet. Click &quot;Edit quote&quot; to add some.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="jc-quote-readonly">
            {lineItems.map((li, i) => {
              const lt = lineTotalOf(li);
              return (
                <div
                  key={li.line_id || i}
                  className="flex items-start gap-2 text-sm border-b border-slate-100 pb-1.5 last:border-0"
                  data-testid={`jc-quote-line-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-800 font-medium">{li.description}</p>
                    <p className="text-[11px] text-slate-500">
                      {CAT_LABEL[li.category] || 'Item'}
                      {li.quantity ? ` · Qty ${li.quantity}` : ''}
                    </p>
                  </div>
                  <span className="font-mono text-slate-800 whitespace-nowrap">{money(lt)}</span>
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-emerald-200">
              <span className="text-xs font-semibold text-emerald-900">Quote total (inc GST)</span>
              <span className="font-mono font-bold text-emerald-900" data-testid="jc-quote-total">
                {money(quote?.total || total)}
              </span>
            </div>
            {smsBody && (
              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500 bg-slate-50 rounded p-2" data-testid="jc-quote-sms-body">
                <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span className="whitespace-pre-wrap">{smsBody}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
